import { Box, Button, Chip, Divider, Grid } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { CustomLoader } from "../../../Components/CustomLoader";
import CustomTextField from "../../../Components/CustomTextField";
import InventoryServices from "../../../services/InventoryService";
import ProductService from "../../../services/ProductService";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import CustomAutocomplete from "../../../Components/CustomAutocomplete";
import { useNotificationHandling } from "../../../Components/useNotificationHandling ";
import { MessageAlert } from "../../../Components/MessageAlert";
import { DecimalValidation } from "../../../utility/DecimalValidation";
import { Popup } from "../../../Components/Popup";

const Root = styled("div")(({ theme }) => ({
  width: "100%",
  ...theme.typography.body2,
  "& > :not(style) + :not(style)": {
    marginTop: theme.spacing(2),
  },
}));

export const PurchaseOrderCreate = ({
  recordForEdit,
  setVendorData,
  vendorData,
  setOpenPopup,
  getAllVendorDetails,
}) => {
  const { sellerData, userData } = useSelector((state) => ({
    sellerData: state.auth.sellerAccount,
    userData: state.auth.profile,
  }));
  const vendorContacts =
    recordForEdit && Array.isArray(recordForEdit.contacts)
      ? recordForEdit.contacts
      : [];
  const sellerAccounts = Array.isArray(sellerData) ? sellerData : [];
  const [openAlert, setOpenAlert] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [inputValues, setInputValues] = useState({
    created_by: userData ? userData.email : "",
    po_date: today,
    schedule_date: today,
    vendor: recordForEdit ? recordForEdit.name : "",
    vendor_type: recordForEdit ? recordForEdit.type : "",
    vendor_email: "",
    vendor_contact_person: "",
    vendor_contact: "",
    seller_account: "",
    payment_terms: "",
    delivery_terms: "",
    currency: "",
    products: [
      {
        product: "",
        quantity: "",
        unit: "",
        amount: "",
        rate: "",
      },
    ],
  });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [productOption, setProductOption] = useState([]);
  const [currencyOption, setCurrencyOption] = useState([]);

  const { handleSuccess, handleError, handleCloseSnackbar, alertInfo } =
    useNotificationHandling();

  console.log("inputVales", inputValues);

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setInputValues((prevValues) => ({ ...prevValues, [name]: value }));
  }, []);

  const handleAutocompleteChange = useCallback(
    (fieldName, value) => {
      setInputValues((prevValues) => {
        const newValues = { ...prevValues, [fieldName]: value };
        console.log(selectedProducts);
        if (fieldName === "vendor_contact_person") {
          const selectedContact = vendorContacts.find(
            (contact) => contact.name === value,
          );
          newValues.vendor_contact = selectedContact
            ? selectedContact.contact || ""
            : "";
          newValues.vendor_email = selectedContact
            ? selectedContact.email || ""
            : "";

          // Set currency based on vendor type from recordForEdit
          if (recordForEdit && recordForEdit.type === "Domestic") {
            newValues.currency = "INR";
          } else if (recordForEdit && recordForEdit.type === "International") {
            // Remove INR from options or set to a default currency for International
            newValues.currency = ""; // This can be set to a different default if needed
          }
        }

        return newValues;
      });
    },
    [recordForEdit],
  );

  const handleProductChange = (index, field, value) => {
    setInputValues((prevValues) => {
      const updatedProducts = [...prevValues.products];
      const productToUpdate = { ...updatedProducts[index] };

      // Update the field with the new value
      productToUpdate[field] = value;

      // Calculate amount if quantity or rate is changed
      if (field === "quantity" || field === "rate") {
        const quantity = productToUpdate.quantity
          ? parseFloat(productToUpdate.quantity)
          : 0;
        const rate = productToUpdate.rate
          ? parseFloat(productToUpdate.rate)
          : 0;
        productToUpdate.amount = (quantity * rate).toFixed(2); // Use toFixed(2) to format it as a decimal
      }

      updatedProducts[index] = productToUpdate;
      return { ...prevValues, products: updatedProducts };
    });
  };

  const handleProductAutocompleteChange = (index, selectedProductName) => {
    if (!selectedProductName) {
      setInputValues((prevValues) => ({
        ...prevValues,
        products: prevValues.products.map((product, idx) =>
          idx === index
            ? {
                ...product,
                product: "",
                unit: "",
                type_of_unit: "",
                max_decimal_digit: "",
              }
            : product,
        ),
      }));
      return;
    }

    // Find the product object based on the selected value
    const productObj = productOption.find(
      (item) => item.name === selectedProductName,
    );

    // Early exit if the product object is not found
    if (!productObj) {
      console.error("Selected product not found in product options");
      return;
    }

    // Check if the new value is already included in the selected products list to prevent duplicates
    // const isDuplicate = inputValues.products.some(
    //   (product, idx) => product.product === selectedProductName && idx !== index
    // );

    // Safely update the state with the new products list
    setInputValues((prevValues) => {
      const newProducts = prevValues.products.map((product, idx) => {
        if (idx === index) {
          return {
            ...product,
            product: selectedProductName,
            unit: productObj.unit,
            type_of_unit: productObj.type_of_unit,
            max_decimal_digit: productObj.max_decimal_digit,
          };
        }
        console.log("inputValues.type_of_unit", inputValues.type_of_unit);
        return product;
      });

      return { ...prevValues, products: newProducts };
    });
  };

  const addProductField = useCallback(() => {
    handleProductChange(inputValues.products.length, "", "");
    setSelectedProducts([...selectedProducts, ""]);
  }, [inputValues.products.length, handleProductChange]);

  const removeProductField = (index) => {
    setInputValues((prevValues) => {
      const products = prevValues.products.filter((_, i) => i !== index);
      const removedProduct = prevValues.products[index].product;

      // Update the selected products list: remove the product that is being deleted
      setSelectedProducts(
        selectedProducts.filter((item) => item !== removedProduct),
      );

      return { ...prevValues, products };
    });
  };

  useEffect(() => {
    if (recordForEdit) {
      setInputValues((prevValues) => ({
        ...prevValues,
        vendor: recordForEdit.name,
        vendor_type: recordForEdit.type,
      }));
    }
  }, [recordForEdit]);

  useEffect(() => {
    if (!recordForEdit) return;
    getProduct();
    getCurrencyDetails();
  }, [recordForEdit]);

  const getCurrencyDetails = async () => {
    setLoading(true);
    try {
      const response = await InventoryServices.getCurrencyData();
      if (response && response.data) {
        let filteredCurrencyOptions = Array.isArray(response.data)
          ? response.data
          : response.data && Array.isArray(response.data.results)
            ? response.data.results
            : [];
        if (recordForEdit.type === "International") {
          // Exclude INR for international vendors
          filteredCurrencyOptions = filteredCurrencyOptions.filter(
            (option) => option.name !== "INR",
          );
        }
        setCurrencyOption(filteredCurrencyOptions);

        // Set default currency to INR if vendor is Domestic
        if (recordForEdit.type === "Domestic" && !inputValues.currency) {
          setInputValues((prevValues) => ({
            ...prevValues,
            currency: "INR",
          }));
        }
      }
    } catch (err) {
      handleError(err);
      console.error("Error fetching currency data", err);
    } finally {
      setLoading(false);
    }
  };

  const getProduct = async () => {
    try {
      setProductLoading(true);
      const res = await ProductService.getAllProduct();
      setProductOption(
        Array.isArray(res.data)
          ? res.data
          : res.data && Array.isArray(res.data.results)
            ? res.data.results
            : [],
      );
    } catch (err) {
      handleError(err);
      console.error("error potential", err);
    } finally {
      setProductLoading(false);
    }
  };
  const openAlertPopup = (e) => {
    e.preventDefault();
    setOpenAlert(true);
  };

  const createPurchaseOrderDetails = async (e) => {
    console.log(vendorData);
    try {
      e.preventDefault();

      setLoading(true);
      const numTypes = inputValues.products.map((item) => item.type_of_unit);
      const quantities = inputValues.products.map((item) => item.quantity);
      const decimalCounts = inputValues.products.map((item) =>
        String(item.max_decimal_digit),
      );
      const unit = inputValues.products.map((item) => item.unit);

      const isvalid = DecimalValidation({
        numTypes,
        quantities,
        decimalCounts,
        unit,
        handleError,
      });
      if (!isvalid) {
        setLoading(false);
        return;
      }

      const req = {
        created_by: inputValues.created_by,
        vendor: inputValues.vendor,
        vendor_type: inputValues.vendor_type,
        vendor_email: inputValues.vendor_email,
        vendor_contact_person: inputValues.vendor_contact_person,
        vendor_contact: inputValues.vendor_contact,
        seller_account: inputValues.seller_account,
        payment_terms: inputValues.payment_terms,
        delivery_terms: inputValues.delivery_terms,
        schedule_date: inputValues.schedule_date,
        currency: inputValues.currency,
        po_date: inputValues.po_date,
        products: inputValues.products || [],
      };

      const response = await InventoryServices.createPurchaseOrderData(req);

      if (response) {
        getAllVendorDetails();
      }

      handleSuccess("Purchase Order Created Successfully");
      setTimeout(() => {
        setOpenPopup(false);
      }, 300);
      setLoading(false);
    } catch (error) {
      handleError(error);
      console.log("creating Purchase Order error", error);
      setLoading(false);
    }
  };

  return (
    <>
      <MessageAlert
        open={alertInfo.open}
        onClose={handleCloseSnackbar}
        severity={alertInfo.severity}
        message={alertInfo.message}
      />
      <CustomLoader open={loading || productLoading} />

      <Box component="form" noValidate onSubmit={(e) => openAlertPopup(e)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <CustomTextField
              fullWidth
              size="small"
              label="Vendor"
              variant="outlined"
              value={recordForEdit ? recordForEdit.name || "" : ""}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <CustomAutocomplete
              size="small"
              disablePortal
              id="combo-box-demo"
              options={vendorContacts}
              getOptionLabel={(option) => option.name}
              onChange={(event, value) =>
                handleAutocompleteChange(
                  "vendor_contact_person",
                  value ? value.name : "",
                )
              }
              label="Vendor Contact Person"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <CustomTextField
              disabled
              fullWidth
              size="small"
              label="Vendor Type"
              variant="outlined"
              value={inputValues.vendor_type || ""}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <CustomAutocomplete
              size="small"
              disablePortal
              id="combo-box-demo"
              onChange={(event, value) =>
                handleAutocompleteChange("seller_account", value)
              }
              options={sellerAccounts.map((option) => option.unit)}
              getOptionLabel={(option) => option}
              sx={{ minWidth: 300 }}
              label="Buyer Account"
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <CustomAutocomplete
              size="small"
              disablePortal
              id="combo-box-demo"
              onChange={(event, value) =>
                setInputValues({ ...inputValues, payment_terms: value })
              }
              options={paymentTerms.map((option) => option)}
              getOptionLabel={(option) => option}
              sx={{ minWidth: 300 }}
              label="Payment Terms"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <CustomAutocomplete
              size="small"
              disablePortal
              id="combo-box-demo"
              onChange={(event, value) =>
                setInputValues({ ...inputValues, delivery_terms: value })
              }
              options={deliveryTerms.map((option) => option)}
              getOptionLabel={(option) => option}
              sx={{ minWidth: 300 }}
              label="Delivery Terms"
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <CustomTextField
              fullWidth
              disabled
              type="date"
              name="po_date"
              size="small"
              label="Purchase Order Date"
              variant="outlined"
              value={inputValues.po_date}
              InputProps={{ inputProps: { min: today, max: today } }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <CustomAutocomplete
              size="small"
              disablePortal
              id="currency-autocomplete"
              value={
                currencyOption.find((c) => c.name === inputValues.currency) ||
                null
              }
              onChange={(event, value) =>
                setInputValues({
                  ...inputValues,
                  currency: value ? value.name : "",
                })
              }
              options={currencyOption.map((option) => option)}
              getOptionLabel={(option) => `${option.name} (${option.symbol})`}
              sx={{ minWidth: 300 }}
              label="Currency"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <CustomTextField
              fullWidth
              type="date"
              name="schedule_date"
              size="small"
              label="Schedule Date"
              variant="outlined"
              value={inputValues.schedule_date}
              onChange={handleInputChange}
              InputProps={{ inputProps: { min: today } }} // Restrict past dates
            />
          </Grid>

          <Grid item xs={12}>
            <Root>
              <Divider>
                <Chip label="PRODUCT" />
              </Divider>
            </Root>
          </Grid>
          {inputValues.products.map((input, index) => {
            return (
              <>
                <Grid key={index} item xs={12} sm={3}>
                  <CustomAutocomplete
                    name="product"
                    size="small"
                    disablePortal
                    id={`combo-box-demo-${index}`}
                    value={input.product ? input.product : ""}
                    onChange={(event, value) =>
                      handleProductAutocompleteChange(index, value)
                    }
                    options={productOption.map((option) => option.name)}
                    getOptionLabel={(option) => option}
                    sx={{ minWidth: 300 }}
                    label="Product"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <CustomTextField
                    fullWidth
                    name="unit"
                    size="small"
                    label="Unit"
                    variant="outlined"
                    value={input.unit ? input.unit : ""}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <CustomTextField
                    fullWidth
                    name="quantity"
                    step={input.type_of_unit === "decimal" ? "0.01" : "1"}
                    size="small"
                    label="Quantity"
                    variant="outlined"
                    value={
                      input.type_of_unit === "decimal"
                        ? input.quantity || ""
                        : Math.floor(input.quantity || "")
                    }
                    onChange={(event) =>
                      handleProductChange(index, "quantity", event.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <CustomTextField
                    fullWidth
                    name="rate"
                    size="small"
                    label="Rate"
                    variant="outlined"
                    value={input.rate || ""}
                    onChange={(event) =>
                      handleProductChange(index, "rate", event.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <CustomTextField
                    fullWidth
                    disabled
                    name="amount"
                    size="small"
                    label="Amount"
                    variant="outlined"
                    value={input.amount || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={1} alignContent="right">
                  {index !== 0 && (
                    <Button
                      disabled={index === 0}
                      onClick={() => removeProductField(index)}
                      variant="contained"
                    >
                      Remove
                    </Button>
                  )}
                </Grid>
              </>
            );
          })}

          <Grid item xs={12} sm={2} alignContent="right">
            <Button
              onClick={addProductField}
              variant="contained"
              sx={{ marginRight: "1em" }}
            >
              Add More...
            </Button>
          </Grid>
        </Grid>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
        >
          Submit
        </Button>
      </Box>

      <Popup
        openPopup={openAlert}
        setOpenPopup={setOpenAlert}
        title="PO Confirmation"
      >
        <Box sx={{ mb: 2 }}>
          <p
            style={{ fontWeight: 500, marginBottom: "10px", color: "#ff2121" }}
          >
            Note: Please review the following products before creating the
            packing list.{" "}
            <p>Once submitted, the quantities will be processed.</p>
          </p>

          {inputValues.products.length > 0 ? (
            inputValues.products.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  mb: 1,
                  background: "#f9fafb",
                }}
              >
                <span style={{ fontWeight: 500, color: "#333" }}>
                  {item.product}
                </span>

                <span style={{ color: "#1976d2", fontWeight: 600 }}>
                  Qty: {item.quantity}
                </span>
              </Box>
            ))
          ) : (
            <p style={{ color: "#d32f2f" }}>No products added.</p>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            sx={{
              backgroundColor: "#fd0000",
              color: "#ffffff",
              border: "none",
              "&:hover": {
                borderColor: "#616161",
                backgroundColor: "#ff3d3d",
              },
            }}
            onClick={() => setOpenAlert(false)}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#115293",
              },
            }}
            onClick={createPurchaseOrderDetails}
          >
            Confirm & Create
          </Button>
        </Box>
      </Popup>
    </>
  );
};

// Data structure for Payment Terms
const paymentTerms = [
  "100% Advance against PI",
  "50% Advance, Balance Before Dispatch",
  "30% Advance, Balance Before Dispatch",
  "15 days with PDC (Post-Dated Check)",
  "30 days with PDC",
  "45 days with PDC",
  "60 days with PDC",
  "15 days credit",
  "30 days credit",
  "45 days credit",
  "60 days credit",
  "90 days credit",
  "120 days credit",
  "TT in 100% advance against PI",
  "10% Advance, balance against bill of lading",
  "15% Advance, balance against bill of lading",
  "20% Advance, balance against bill of lading",
  "30% Advance, balance against bill of lading",
  "50% Advance, balance against bill of lading",
  "60 days against documents",
  "90 days against documents",
  "120 days against documents",
  "LC at Sight",
  "LC 45 days",
  "LC 60 days",
];

// Data structure for Delivery Terms
const deliveryTerms = [
  "Ex-Work",
  "FOB (Free On Board)",
  "CIF (Cost, Insurance, and Freight)",
  "C & F (Cost and Freight)",
  "Door Delivery (Prepaid)",
  "Door Delivery to pay",
  "Transporter Warehouse (Prepaid)",
  "Transporter Warehouse to pay",
  "Add actual freight in invoice",
  "Ex - warehouse",
];
